import { useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../constants/firebase";
const DRAFT = "zylen-event-booking-draft";
const PENDING = "zylen-event-booking-pending";
const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "filmmaker",
  marketing_opt_in: false,
  terms: false,
};
const storage = {
  read(key) {
    try {
      return JSON.parse(sessionStorage.getItem(key) || "null");
    } catch {
      return null;
    }
  },
  write(key, value) {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {}
  },
  remove(key) {
    try {
      sessionStorage.removeItem(key);
    } catch {}
  },
};
const call = async (name, data) =>
  (await httpsCallable(functions, name)(data)).data;
function clearReturnURL() {
  const url = new URL(window.location.href);
  for (const key of ["eventPayment", "booking", "reference", "trxref"])
    url.searchParams.delete(key);
  window.history.replaceState(
    window.history.state,
    "",
    `${url.pathname}${url.search}${url.hash}`,
  );
}
export default function useEventTicketPayment() {
  const [draft] = useState(() => storage.read(DRAFT));
  const [form, setForm] = useState(() => ({
    ...EMPTY,
    ...(draft?.form || {}),
  }));
  const [quantity, setQuantity] = useState(() =>
    Math.min(6, Math.max(1, Number(draft?.quantity) || 1)),
  );
  const [price, setPrice] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [payment, setPayment] = useState({ status: "" });
  const locked = useRef(false);
  const refreshing = useRef(false);
  const refreshQueued = useRef(false);
  const availabilityRevision = useRef(0);
  const mounted = useRef(false);
  const referenceRef = useRef("");
  const refreshAvailability = useCallback(async () => {
    if (refreshing.current) {
      refreshQueued.current = true;
      return;
    }
    refreshing.current = true;
    const revision = availabilityRevision.current;
    try {
      const data = await call("getEventTicketConfig");
      if (!mounted.current || revision !== availabilityRevision.current) return;
      if (
        !Number.isSafeInteger(data.availableSeats) ||
        data.availableSeats < 0
      ) {
        throw new Error("Seat availability is not configured.");
      }
      setPrice(data.unitPrice);
      setAvailability(data);
    } catch (e) {
      if (mounted.current && revision === availabilityRevision.current) {
        setError(e.message || "Ticket availability is currently unavailable.");
      }
    } finally {
      refreshing.current = false;
      if (refreshQueued.current && mounted.current) {
        refreshQueued.current = false;
        void refreshAvailability();
      }
    }
  }, []);
  const verify = useCallback(
    async (reference, cancelled = false) => {
      if (locked.current || !reference) return;
      locked.current = true;
      setBusy(true);
      setError("");
      try {
        const result = await call("verifyEventTicketPayment", { reference });
        if (!mounted.current) return;
        if (result.status === "paid") {
          if (Number.isSafeInteger(result.availability?.availableSeats)) {
            // This snapshot is obtained AFTER the server commits the seat booking.
            // Ignore any older availability request that is still in flight.
            availabilityRevision.current += 1;
            setAvailability((previous) => ({
              ...previous,
              ...result.availability,
            }));
            if (typeof result.availability.unitPrice === "number") {
              setPrice(result.availability.unitPrice);
            }
          }
          setPayment(result);
          storage.remove(DRAFT);
          storage.remove(PENDING);
          clearReturnURL();
        } else if (result.status === "requires_review") {
          setPayment({
            ...result,
            message:
              "Your payment was received, but seats could not be confirmed. The event team has been alerted. Please contact them with your reference; do not pay again.",
          });
          clearReturnURL();
        } else if (result.status === "unpaid") {
          setPayment({
            status: "cancelled",
            message:
              "Checkout was cancelled or not completed. Your details have been kept.",
          });
          clearReturnURL();
        } else {
          setPayment({
            status: "pending",
            message: cancelled
              ? "Checkout was cancelled or closed. Payment is not confirmed yet; check again before starting another payment."
              : "Payment is not confirmed yet. Please check again shortly. Do not pay again while confirmation is pending.",
          });
        }
      } catch (e) {
        if (mounted.current)
          setPayment({
            status: "unknown",
            message:
              "We could not confirm the payment yet. Please check again before making another payment.",
          });
      } finally {
        locked.current = false;
        if (mounted.current) {
          setBusy(false);
          void refreshAvailability();
        }
      }
    },
    [refreshAvailability],
  );
  useEffect(() => {
    mounted.current = true;
    void refreshAvailability();
    const restore = () => {
      const params = new URLSearchParams(window.location.search);
      const reference = params.get("booking") || storage.read(PENDING);
      if (
        typeof reference === "string" &&
        /^zyev-[a-f0-9]{32}$/.test(reference)
      ) {
        referenceRef.current = reference;
        void verify(reference, params.get("eventPayment") !== "return");
        document.getElementById("book")?.scrollIntoView({ block: "start" });
      }
    };
    const timer = setTimeout(restore, 0);
    const pageShow = (e) => {
      if (e.persisted) {
        locked.current = false;
        restore();
      }
    };
    const visible = () => {
      if (document.visibilityState === "visible") void refreshAvailability();
    };
    const poll = setInterval(visible, 20000);
    window.addEventListener("pageshow", pageShow);
    document.addEventListener("visibilitychange", visible);
    return () => {
      mounted.current = false;
      clearTimeout(timer);
      clearInterval(poll);
      window.removeEventListener("pageshow", pageShow);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [verify, refreshAvailability]);
  const change = (e) => {
    const { name, type, checked, value } = e.target;
    setForm((old) => ({
      ...old,
      [name]: type === "checkbox" ? checked : value,
    }));
    setError("");
  };
  const availableSeats = availability?.availableSeats ?? null;
  const soldOut = availability?.soldOut === true;
  const maxTickets = Math.min(6, availableSeats ?? 0);
  const submit = async (e) => {
    e.preventDefault();
    if (
      locked.current ||
      price === null ||
      ["paid", "pending", "unknown", "requires_review"].includes(payment.status)
    )
      return;
    if (availableSeats === null || quantity > maxTickets) {
      setError(
        soldOut
          ? "Seats full. This event is sold out."
          : availableSeats === 0
            ? "The remaining seats are temporarily reserved. Please check again shortly."
            : `Only ${maxTickets} ticket(s) can currently be booked. Please adjust the quantity.`,
      );
      void refreshAvailability();
      return;
    }
    if (
      !form.first_name.trim() ||
      !form.last_name.trim() ||
      !/^\S+@\S+\.\S+$/.test(form.email.trim()) ||
      !form.terms
    ) {
      setError(
        "Please enter your name and a valid email, and accept the ticket terms.",
      );
      return;
    }
    locked.current = true;
    setBusy(true);
    setError("");
    storage.write(DRAFT, { form, quantity });
    try {
      const result = await call("initializeEventTicketPayment", {
        ...form,
        quantity,
        expectedUnitMinor: Math.round(price * 100),
      });
      const url = new URL(result.authorizationUrl);
      if (url.origin !== "https://checkout.paystack.com")
        throw new Error("Invalid payment destination.");
      storage.write(PENDING, result.reference);
      referenceRef.current = result.reference;
      window.location.assign(url.href);
    } catch (e) {
      if (mounted.current) {
        setError(e.message || "Could not open checkout. Please try again.");
        void refreshAvailability();
      }
    } finally {
      locked.current = false;
      if (mounted.current) setBusy(false);
    }
  };
  const reset = () => {
    storage.remove(DRAFT);
    storage.remove(PENDING);
    clearReturnURL();
    referenceRef.current = "";
    setPayment({ status: "" });
    setError("");
    setForm({ ...EMPTY });
    setQuantity(1);
    void refreshAvailability();
  };
  return {
    form,
    quantity,
    setQuantity,
    price,
    error,
    busy,
    payment,
    change,
    submit,
    reset,
    availableSeats,
    soldOut,
    maxTickets,
    refreshAvailability,
    retryVerification: () => verify(referenceRef.current),
  };
}
